import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactFlow, {
  Background,
  Controls,
  Handle,
  Position,
  ReactFlowProvider,
  addEdge,
  useEdgesState,
  useNodesState,
  useReactFlow
} from 'reactflow';
import 'reactflow/dist/style.css';
import { config } from '../../config/config';
import { showToast } from '../utils/Toast';
import LoadingSpinner from '../common/LoadingSpinner';

const NODE_WIDTH = 220;
const NODE_HEIGHT = 60;
const H_GAP = 32;
const V_GAP = 60;
const DRAG_MIME = 'application/x-org-user-id';

// Crew roles are no longer surfaced in settings
const HIDDEN_ROLES = new Set(['crew_leader', 'crew_member']);
const isHiddenRoleUser = (u) => HIDDEN_ROLES.has(u?.profile?.role);

// Build {nodes, edges} from a list of users that have manager relationships.
// Only users who are part of any relationship (have a manager OR are a manager) are included.
const buildGraphFromUsers = (users) => {
  const byId = new Map(users.map((u) => [String(u._id), u]));
  const managerIdOf = (u) => {
    if (!u.manager) return null;
    const mid = typeof u.manager === 'object' ? u.manager._id || u.manager.id : u.manager;
    return mid ? String(mid) : null;
  };

  // Determine which users participate
  const involved = new Set();
  users.forEach((u) => {
    const mid = managerIdOf(u);
    if (mid && byId.has(mid)) {
      involved.add(String(u._id));
      involved.add(mid);
    }
  });

  if (involved.size === 0) return { nodes: [], edges: [] };

  // Build children map for layout
  const children = new Map();
  involved.forEach((id) => children.set(id, []));
  involved.forEach((id) => {
    const mid = managerIdOf(byId.get(id));
    if (mid && involved.has(mid)) {
      children.get(mid).push(id);
    }
  });

  // Roots: involved users with no manager (or manager not involved)
  const roots = Array.from(involved).filter((id) => {
    const mid = managerIdOf(byId.get(id));
    return !mid || !involved.has(mid);
  });

  // Compute subtree widths then x positions (simple tree layout)
  const subtreeWidth = new Map();
  const computeWidth = (id) => {
    const kids = children.get(id) || [];
    if (kids.length === 0) {
      subtreeWidth.set(id, NODE_WIDTH);
      return NODE_WIDTH;
    }
    let total = 0;
    kids.forEach((k, i) => {
      total += computeWidth(k);
      if (i > 0) total += H_GAP;
    });
    const w = Math.max(NODE_WIDTH, total);
    subtreeWidth.set(id, w);
    return w;
  };
  roots.forEach(computeWidth);

  const positions = new Map();
  const place = (id, xLeft, depth) => {
    const w = subtreeWidth.get(id);
    const cx = xLeft + w / 2;
    positions.set(id, { x: cx - NODE_WIDTH / 2, y: depth * (NODE_HEIGHT + V_GAP) });
    let cursor = xLeft;
    (children.get(id) || []).forEach((k) => {
      const cw = subtreeWidth.get(k);
      place(k, cursor, depth + 1);
      cursor += cw + H_GAP;
    });
  };
  let xCursor = 0;
  roots.forEach((r) => {
    place(r, xCursor, 0);
    xCursor += subtreeWidth.get(r) + H_GAP * 2;
  });

  const nodes = Array.from(involved).map((id) => ({
    id,
    type: 'user',
    position: positions.get(id) || { x: 0, y: 0 },
    data: { user: byId.get(id) }
  }));

  const edges = [];
  involved.forEach((id) => {
    const mid = managerIdOf(byId.get(id));
    if (mid && involved.has(mid)) {
      edges.push({
        id: `e-${mid}-${id}`,
        source: mid,
        target: id,
        type: 'smoothstep',
        style: { stroke: '#94a3b8', strokeWidth: 1.5 }
      });
    }
  });

  return { nodes, edges };
};

const roleLabels = {
  super_admin: 'Super Admin',
  admin: 'Admin',
  project_manager: 'Project Manager',
  crew_leader: 'Crew Leader',
  crew_member: 'Crew Member'
};

const roleColor = (role) => {
  switch (role) {
    case 'super_admin':
      return 'bg-purple-100 text-purple-800 border-purple-200';
    case 'admin':
      return 'bg-blue-100 text-blue-800 border-blue-200';
    case 'project_manager':
      return 'bg-emerald-100 text-emerald-800 border-emerald-200';
    case 'crew_leader':
      return 'bg-amber-100 text-amber-800 border-amber-200';
    case 'crew_member':
      return 'bg-gray-100 text-gray-700 border-gray-200';
    default:
      return 'bg-gray-100 text-gray-700 border-gray-200';
  }
};

const fullName = (u) =>
  `${u.profile?.firstName || ''} ${u.profile?.lastName || ''}`.trim() || u.email;

// ---------- node ----------

const UserNode = ({ data, id }) => {
  const { user, onRemove } = data;
  const role = user.profile?.role;
  return (
    <div
      className="rounded-md border bg-white shadow-sm group"
      style={{ width: NODE_WIDTH, minHeight: NODE_HEIGHT }}
    >
      <Handle type="target" position={Position.Top} className="!bg-blue-500 !w-2 !h-2" />
      <div className="flex items-center gap-2 px-3 py-2 min-w-0">
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium text-gray-900 truncate">{fullName(user)}</div>
          <div className="text-xs text-gray-500 truncate">{user.email}</div>
        </div>
        <span
          className={`shrink-0 inline-block text-[10px] px-1.5 py-0.5 rounded border ${roleColor(role)}`}
        >
          {roleLabels[role] || role}
        </span>
        <button
          type="button"
          onClick={() => onRemove && onRemove(id)}
          title="Remove from canvas"
          className="shrink-0 opacity-0 group-hover:opacity-100 transition-opacity text-gray-400 hover:text-red-500 leading-none"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </button>
      </div>
      <Handle type="source" position={Position.Bottom} className="!bg-blue-500 !w-2 !h-2" />
    </div>
  );
};

const nodeTypes = { user: UserNode };

// ---------- left list ----------

const ListItem = ({ user, isOnCanvas }) => (
  <div
    draggable
    onDragStart={(e) => {
      e.dataTransfer.setData(DRAG_MIME, user._id);
      e.dataTransfer.effectAllowed = 'move';
    }}
    className={`rounded-md border bg-white px-3 py-1.5 cursor-grab active:cursor-grabbing hover:bg-gray-50 ${
      isOnCanvas ? 'opacity-60' : ''
    }`}
  >
    <div className="flex items-center gap-2 min-w-0">
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium text-gray-900 truncate">{fullName(user)}</div>
        <div className="text-xs text-gray-500 truncate">{user.email}</div>
      </div>
      <span
        className={`shrink-0 inline-block text-[10px] px-1.5 py-0.5 rounded border ${roleColor(user.profile?.role)}`}
      >
        {roleLabels[user.profile?.role] || user.profile?.role}
      </span>
    </div>
  </div>
);

// ---------- canvas ----------

const Canvas = ({ users, nodes, edges, setNodes, setEdges, onNodesChange, onEdgesChange }) => {
  const wrapperRef = useRef(null);
  const { screenToFlowPosition } = useReactFlow();

  const onRemoveNode = useCallback(
    (id) => {
      setNodes((nds) => nds.filter((n) => n.id !== id));
      setEdges((eds) => eds.filter((e) => e.source !== id && e.target !== id));
    },
    [setNodes, setEdges]
  );

  // Inject the stable callback into data at render time so it's always fresh.
  const nodesWithRemove = useMemo(
    () => nodes.map((n) => ({ ...n, data: { ...n.data, onRemove: onRemoveNode } })),
    [nodes, onRemoveNode]
  );

  const onConnect = useCallback(
    (params) => {
      const { source, target } = params;
      if (!source || !target || source === target) return;

      // Cycle check using current edges (each node has at most one parent → one incoming edge).
      const parentOf = new Map();
      edges.forEach((e) => parentOf.set(e.target, e.source));
      let cursor = source;
      const seen = new Set();
      while (cursor) {
        if (cursor === target) {
          showToast('Cannot connect: would create a cycle', 'error');
          return;
        }
        if (seen.has(cursor)) break;
        seen.add(cursor);
        cursor = parentOf.get(cursor);
      }

      // A node can have at most one manager → drop existing incoming edge
      setEdges((eds) =>
        addEdge(
          { ...params, type: 'smoothstep', style: { stroke: '#94a3b8', strokeWidth: 1.5 } },
          eds.filter((e) => e.target !== target)
        )
      );
    },
    [edges, setEdges]
  );

  const onCanvasDrop = useCallback(
    (event) => {
      event.preventDefault();
      const userId = event.dataTransfer.getData(DRAG_MIME);
      if (!userId) return;
      // Already on canvas?
      if (nodes.some((n) => n.id === userId)) return;

      const user = users.find((u) => u._id === userId);
      if (!user) return;

      const position = screenToFlowPosition({
        x: event.clientX,
        y: event.clientY
      });

      setNodes((nds) =>
        nds.concat({
          id: user._id,
          type: 'user',
          position: { x: position.x - NODE_WIDTH / 2, y: position.y - NODE_HEIGHT / 2 },
          data: { user }
        })
      );
    },
    [nodes, users, screenToFlowPosition, setNodes]
  );

  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onNodesDelete = useCallback(
    (deleted) => {
      const ids = new Set(deleted.map((n) => n.id));
      setEdges((eds) => eds.filter((e) => !ids.has(e.source) && !ids.has(e.target)));
    },
    [setEdges]
  );

  return (
    <div ref={wrapperRef} className="w-full h-full" onDrop={onCanvasDrop} onDragOver={onDragOver}>
      <ReactFlow
        nodes={nodesWithRemove}
        edges={edges}
        nodeTypes={nodeTypes}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onNodesDelete={onNodesDelete}
        deleteKeyCode={['Backspace', 'Delete']}
        fitView
        proOptions={{ hideAttribution: true }}
      >
        <Background gap={16} color="#e5e7eb" />
        <Controls showInteractive={false} />
      </ReactFlow>
    </div>
  );
};

// ---------- main ----------

const OrgHierarchyView = ({ isMaximized = true }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');

  // Canvas starts empty; auto-populated from saved hierarchy on first load.
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const didAutoLoadRef = useRef(false);

  const fetchUsers = async ({ populateCanvas = false } = {}) => {
    setLoading(true);
    try {
      const res = await fetch(`${config.apiUrl}/api/settings/users/hierarchy`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to load users');
      const list = (data.users || []).filter((u) => !isHiddenRoleUser(u));
      setUsers(list);
      if (populateCanvas) {
        const { nodes: ns, edges: es } = buildGraphFromUsers(list);
        setNodes(ns);
        setEdges(es);
      }
      return list;
    } catch (err) {
      showToast(err.message || 'Failed to load users', 'error');
      return [];
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      const list = await fetchUsers();
      if (!didAutoLoadRef.current) {
        didAutoLoadRef.current = true;
        const { nodes: ns, edges: es } = buildGraphFromUsers(list);
        if (ns.length > 0) {
          setNodes(ns);
          setEdges(es);
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLoadSaved = async () => {
    if (nodes.length > 0) {
      if (!window.confirm('Replace current canvas with the saved hierarchy?')) return;
    }
    const list = await fetchUsers();
    const { nodes: ns, edges: es } = buildGraphFromUsers(list);
    setNodes(ns);
    setEdges(es);
    if (ns.length === 0) showToast('No saved hierarchy yet', 'info');
  };

  const onCanvasIds = useMemo(() => new Set(nodes.map((n) => n.id)), [nodes]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const arr = [...users].sort((a, b) =>
      fullName(a).toLowerCase().localeCompare(fullName(b).toLowerCase())
    );
    if (!q) return arr;
    return arr.filter(
      (u) =>
        fullName(u).toLowerCase().includes(q) ||
        (u.email || '').toLowerCase().includes(q) ||
        (u.profile?.role || '').toLowerCase().includes(q)
    );
  }, [users, search]);

  const handleClearCanvas = () => {
    if (nodes.length === 0) return;
    if (!window.confirm('Clear all nodes from the canvas? Unsaved changes will be lost.')) return;
    setNodes([]);
    setEdges([]);
  };

  const handleSave = async () => {
    if (nodes.length === 0) {
      showToast('Add at least one node to the canvas before saving', 'error');
      return;
    }

    // Build assignments: every node on canvas → its manager (incoming edge source) or null.
    const parentOf = new Map();
    edges.forEach((e) => parentOf.set(e.target, e.source));

    const assignments = nodes.map((n) => ({
      userId: n.id,
      managerId: parentOf.get(n.id) || null
    }));

    setSaving(true);
    try {
      const res = await fetch(`${config.apiUrl}/api/settings/users/hierarchy`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ assignments, reset: false })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to save hierarchy');
      showToast('Hierarchy saved', 'success');
      // Refresh underlying users so list reflects new manager state
      fetchUsers();
    } catch (err) {
      showToast(err.message || 'Failed to save hierarchy', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div>
      <div className="mb-3 flex items-center justify-between gap-3">
        <div>
          <h3 className="text-lg font-medium text-gray-900">Organizational Hierarchy</h3>
          <p className="text-xs text-gray-500 mt-1">
            Drag members from the left onto the canvas, then drag from a node's bottom handle to
            another node's top handle to set a manager. Click Save to persist.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleLoadSaved}
            disabled={saving}
            className="text-xs px-3 py-1.5 border border-gray-200 rounded hover:bg-gray-50 disabled:opacity-50"
          >
            Load Saved
          </button>
          <button
            type="button"
            onClick={handleClearCanvas}
            disabled={nodes.length === 0 || saving}
            className="text-xs px-3 py-1.5 border border-gray-200 rounded hover:bg-gray-50 disabled:opacity-50"
          >
            Clear
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || nodes.length === 0}
            className="text-xs px-3 py-1.5 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>

      <div className={`grid grid-cols-12 gap-3 ${isMaximized ? 'h-[calc(100vh-300px)] min-h-[400px]' : 'h-[460px]'}`}>
        {/* Left pane */}
        <div className="col-span-4 lg:col-span-3 border border-gray-200 rounded-lg bg-white overflow-hidden flex flex-col">
          <div className="px-3 py-2 border-b border-gray-200 bg-gray-50">
            <div className="text-sm font-semibold text-gray-700">All Members</div>
            <div className="text-[11px] text-gray-500">{users.length} total · {nodes.length} on canvas</div>
          </div>
          <div className="p-2 border-b border-gray-200">
            <input
              type="text"
              placeholder="Search members…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
          <div className="flex-1 overflow-y-auto p-2 space-y-1">
            {filtered.length === 0 ? (
              <div className="text-sm text-gray-500 text-center py-6">No members</div>
            ) : (
              filtered.map((u) => (
                <ListItem key={u._id} user={u} isOnCanvas={onCanvasIds.has(u._id)} />
              ))
            )}
          </div>
        </div>

        {/* Right pane: empty canvas */}
        <div className="col-span-8 lg:col-span-9 border border-gray-200 rounded-lg bg-gray-50 overflow-hidden">
          <ReactFlowProvider>
            <Canvas
              users={users}
              nodes={nodes}
              edges={edges}
              setNodes={setNodes}
              setEdges={setEdges}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
            />
          </ReactFlowProvider>
        </div>
      </div>
    </div>
  );
};

export default OrgHierarchyView;
